import { auth } from '../lib/firebase'

export class ApiError extends Error {
  code: string
  status: number
  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

/**
 * The Desk portal the widget is open in, once the bridge has heard it.
 *
 * An agent can belong to two Zoho help desks, and their Zoho connection alone
 * does not say which one the ticket in front of them belongs to. The server
 * checks this against their grant before scoping anything to it.
 */
let deskOrg = ''
const listeners = new Set<() => void>()

export function setDeskOrg(id: string) {
  const next = /^[0-9]{3,20}$/.test(id) ? id : ''
  if (next === deskOrg) return
  deskOrg = next
  // Desk sends the ticket after the panel has already rendered, so a plain
  // variable left the mismatch warning invisible until something unrelated
  // repainted. Anything that reads this can now be told.
  listeners.forEach((fn) => fn())
}

export function getDeskOrg() {
  return deskOrg
}

export function watchDeskOrg(fn: () => void) {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/**
 * Every backend call goes through here so the ID token is attached in exactly
 * one place. In dev the /api prefix is proxied by Vite to the Functions
 * emulator, which keeps the request same-origin https.
 */
export async function apiFetch<T>(path: string, body?: unknown): Promise<T> {
  const user = auth.currentUser
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  if (user) {
    headers.Authorization = `Bearer ${await user.getIdToken()}`
  }

  if (deskOrg) headers['X-Desk-Org'] = deskOrg

  const res = await fetch(`/api/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  const text = await res.text()
  let data: unknown
  try {
    data = JSON.parse(text)
  } catch {
    // A proxy error or crashed emulator returns HTML — showing the start of it
    // beats "Unexpected token <".
    throw new ApiError(res.status, 'bad_response', `Backend error (${res.status}): ${text.slice(0, 120)}`)
  }

  if (!res.ok) {
    const e = data as { error?: string; code?: string }
    throw new ApiError(res.status, e.code ?? 'unknown', e.error ?? `Request failed (${res.status})`)
  }

  return data as T
}
